<template>
  <el-header class="tw-header">
    <div class="left-area flex-grow">
      <slot name="left" />
    </div>
    <div class="right-area flex align-center gap-16">
      <!-- 情報アイコン -->
      <div class="user-info">
        <el-popover
          :model="notificationContentFlag"
          placement="bottom-end"
          width="200"
          trigger="click"
          popper-class="user-popover"
        >
          <div class="popover-content">
            <div>Information</div>
          </div>
          <!-- アバター＋名前がホバー対象 -->
          <div class="user-trigger" slot="reference">
            <i class="el-icon-info primary-icon" />
          </div>
        </el-popover>
      </div>

      <!-- 通知アイコン + バッジ -->
      <el-dropdown
        trigger="click"
        placement="bottom-start"
        :tabindex="-1"
        @visible-change="onNoticeVisibleChange"
      >
        <div class="notification">
          <el-badge
            v-if="notificationCount > 0"
            :value="notificationCount"
            class="badge-wrapper"
          >
            <el-button
              class="notification-button"
              type="text"
              icon="el-icon-bell"
              slot="reference"
            />
          </el-badge>
          <el-button
            v-else
            class="notification-button"
            type="text"
            icon="el-icon-bell"
            slot="reference"
          />
        </div>
        <el-dropdown-menu class="notice_dropdown">
          <tw-notice-list ref="noticeList" />
        </el-dropdown-menu>
      </el-dropdown>

      <!-- ユーザー情報 -->
      <div>
        <el-dropdown @command="handleCommand">
          <div class="flex align-center text-12 cursor-pointer">
            <div
              v-show="userProfile.userIcon || userProfile.userIconDefault"
              class="mr-2"
              style="width:20px;height:20px;border-radius: 50%;overflow: hidden;"
            >
              <img
                :src="userProfile.userIcon || userProfile.userIconDefault"
                v-default-src="'user'"
                style="width: 100%;"
              />
            </div>
            {{ userProfile.userName }}
            <i class="el-icon-arrow-down el-icon--right" />
          </div>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="profile">{{$t('Label.Profile')}}</el-dropdown-item>
            <el-dropdown-item command="logout">{{$t('Label.Sign out')}}</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>

      <!-- 会社 / セクション情報 -->
      <div>
        <el-popover
          placement="bottom-end"
          width="200"
          trigger="click"
          popper-class="user-popover"
        >
          <div class="text-12">
            <!-- Company Name -->
            <div class="text-gray-600">Company Name</div>
            <div>{{ companyShortName }}</div>

            <!-- Organization Name -->
            <div class="text-gray-600 mt-2">Organization Name</div>
            <div
              v-for="userAssign in userProfile.userAssigns"
              :key="userAssign.organizationId"
            >
              {{ userAssign[localeLanguage].organizationName }}
            </div>

            <!-- Company Roles -->
            <div class="text-gray-600 mt-2">Company Roles</div>
            <div>{{ companyRolesString }}</div>

            <!-- User Roles -->
            <div class="text-gray-600 mt-2">User Roles</div>
            <div>{{ userRolesString }}</div>
          </div>
          <!-- アバター＋名前がホバー対象 -->
          <div
            class="flex align-center text-12 cursor-pointer"
            slot="reference"
          >
            <!-- <el-avatar class="tiny-avatar">{{ avatarText }}</el-avatar> -->
            <div
              v-show="userProfile.userIcon || userProfile.userIconDefault"
              class="mr-2"
              style="width:20px;height:20px;border-radius: 50%;overflow: hidden;"
            >
              <img
                :src="userProfile.companyIcon || '/images/company.svg'"
                style="width: 100%;"
              />
            </div>
            {{ companyShortName }}
            <i class="el-icon-arrow-down el-icon--right" />
          </div>
        </el-popover>
      </div>
    </div>
  </el-header>
</template>
<script>
import { defineComponent, ref, computed } from '@vue/composition-api';
import { $api } from '@/store/ApiClient';
import TwNoticeList from '@/components/molecules/TwNoticeList';

import {
  LANGUAGE_SETTING,
  COMPANY_ROLE_CD,
  COMPANY_ROLE_CD_VARIABLES_OPTIONS,
  USER_ROLE_CD_VARIABLES_OPTIONS,
} from 'lib-tw-common';

export default defineComponent({
  name: 'TwHeader',
  components: {
    TwNoticeList,
  },
  props: {
    userName: String,
    sectionName: String,
    notificationCount: {
      type: Number,
      default: 0,
    },
  },
  setup(props, { emit, root }) {
    const noticeList = ref(null);
    const notificationContentFlag = ref(false);

    const localeLanguage = computed(() => {
      return root.$store.state.language === LANGUAGE_SETTING.ENGLISH
        ? 'en'
        : 'local';
    });

    const userProfile = computed(() => root.$store.state.userProfile);

    const avatarText = computed(() =>
      userProfile?.userIcon ? userProfile.userName.slice(0, 1) : 'U',
    );

    const companyShortName = computed(() => {
      return userProfile.value[localeLanguage.value]?.companyShortName || '';
    });

    const companyRolesString = computed(() => {
      return _.uniq(
        _.map(root.$store.state.companyRoles, role =>
          _.get(
            _.find(COMPANY_ROLE_CD_VARIABLES_OPTIONS, { code: role }),
            'label',
          ),
        ),
      ).join(', ');
    });

    const userRolesString = computed(() => {
      return _.uniq(
        _.map(_.get(root.$store.state, 'roleInfo.all') || [], role =>
          _.get(
            _.find(USER_ROLE_CD_VARIABLES_OPTIONS, { code: role.userRoleCd }),
            'label',
          ),
        ),
      ).join(', ');
    });

    const handleCommand = command => {
      if (command === 'profile') {
        root.$router.push({ name: 'UserProfile' });
        emit('profile');
      }
      if (command === 'logout') {
        $api.signOut();
        emit('logout');
      }
    };
    const onNotifyClick = () => {
      emit('notify');
    };

    const onInfoClick = () => {
      emit('info');
    };

    const onNoticeVisibleChange = visible => {
      if (visible) {
        noticeList.value?.fetch();
      }
    };

    return {
      notificationContentFlag,
      noticeList,
      avatarText,
      localeLanguage,
      userProfile,
      companyShortName,
      companyRolesString,
      userRolesString,
      handleCommand,
      onNotifyClick,
      onInfoClick,
      onNoticeVisibleChange,
    };
  },
});
</script>
<style scoped>
.tw-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 36px !important;
  padding: 0 20px;
  border-bottom: 1px solid #eee;
  background-color: #fff;
}

.el-dropdown-link {
  display: flex;
  align-items: center;
  font-size: 12px;
  cursor: pointer;
}

.notification-button {
  font-size: 20px !important;
  margin-right: 4px;
}
.details {
  display: flex;
  flex-direction: column;
}
.primary-icon {
  color: rgb(9, 116, 232);
}
</style>
